"use client";

import {useEffect, useState} from "react";
import {useTranslation} from "react-i18next";
import MissingVoiceDialog from "@/components/inbound/script/MissingVoiceDialog";
import VoiceSelector from "@/components/inbound/script/VoiceSelector";
import {DEFAULT_VOICE_FILTERS} from "@/components/inbound/script/VoiceSelectorFilters";
import Icon from "@/components/library/Icon";
import {AlertDescription, AlertInline} from "@/components/ui/alert-inline";
import type {VoiceVerifyStatus} from "@/hooks/useVerifyScriptVoice";
import type {VoiceOption} from "@/types/agencies";

type ScriptDraftVoiceSectionProps = {
   selectedVoiceId: string;
   onVoiceSelect: (voice: VoiceOption) => void;
   orgId?: string;
   voiceVerifyStatus?: VoiceVerifyStatus;
   voicePromptNonce?: number;
   onChooseVoice?: () => void;
};

export default function ScriptDraftVoiceSection({
   selectedVoiceId,
   onVoiceSelect,
   orgId,
   voiceVerifyStatus = "unknown",
   voicePromptNonce = 0,
   onChooseVoice,
}: Readonly<ScriptDraftVoiceSectionProps>) {
   const {t} = useTranslation("ask");
   const {t: it} = useTranslation("inbound");
   const [dialogClosedThisVisit, setDialogClosedThisVisit] = useState(false);
   const isMissing = voiceVerifyStatus === "missing";

   useEffect(() => {
      if (!isMissing) {
         setDialogClosedThisVisit(false);
      }
   }, [isMissing]);

   useEffect(() => {
      if (voicePromptNonce > 0) {
         setDialogClosedThisVisit(false);
      }
   }, [voicePromptNonce]);

   return (
      <div className="space-y-2">
         <MissingVoiceDialog
            open={isMissing && !dialogClosedThisVisit}
            onOpenChange={(open) => setDialogClosedThisVisit(!open)}
            onChooseVoice={onChooseVoice}
         />
         <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full flex items-center justify-center bg-rose-500">
               <Icon icon="head-side-speak" className="w-4 h-4 text-white" />
            </div>
            <span className="text-sm font-medium text-700">
               {t("workspace.scriptDraft.voiceLabel")}
            </span>
         </div>
         <div className="ml-4 space-y-1.5">
            {isMissing && (
               <AlertInline variant="warning">
                  <Icon icon="triangle-exclamation" className="w-4 h-4" />
                  <AlertDescription>{it("pages.agent.voiceUnavailableInline")}</AlertDescription>
               </AlertInline>
            )}
            <VoiceSelector
               selectedVoiceId={selectedVoiceId}
               onVoiceSelect={onVoiceSelect}
               orgId={orgId}
               searchQuery=""
               filters={DEFAULT_VOICE_FILTERS}
               compact
               voiceUnavailable={isMissing}
            />
            <p className="text-xs text-500">{t("workspace.scriptDraft.voiceHint")}</p>
         </div>
      </div>
   );
}
